import { useRef, useEffect } from 'react'
import { Chart, ArcElement, PieController, Tooltip, Legend } from 'chart.js'
import { useThemeStore } from '../../stores/themeStore'

Chart.register(ArcElement, PieController, Tooltip, Legend)

interface DepartmentSlice {
    department: string
    totalQuantity: number
    inventoryValue: number
    uniqueSKUs: number
}

interface Props {
    data: DepartmentSlice[]
    metric?: 'units' | 'value' | 'skus'
}

/** Premium color palette — curated, harmonious hues */
const PALETTE = [
    'hsl(262, 83%, 58%)',  // violet
    'hsl(217, 91%, 60%)',  // blue
    'hsl(160, 84%, 39%)',  // emerald
    'hsl(38, 92%, 50%)',   // amber
    'hsl(346, 77%, 50%)',  // rose
    'hsl(192, 91%, 36%)',  // teal
    'hsl(25, 95%, 53%)',   // orange
    'hsl(280, 67%, 50%)',  // purple
    'hsl(142, 71%, 45%)',  // green
    'hsl(4, 90%, 58%)',    // red
    'hsl(199, 89%, 48%)',  // sky
    'hsl(47, 96%, 53%)',   // yellow
]

function getMetricValue(d: DepartmentSlice, metric: string): number {
    switch (metric) {
        case 'value': return d.inventoryValue
        case 'skus': return d.uniqueSKUs
        default: return d.totalQuantity
    }
}

function formatValue(v: number, metric: string): string {
    if (metric === 'value') {
        if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`
        if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`
        return `$${v.toLocaleString()}`
    }
    return v.toLocaleString()
}

const METRIC_LABELS: Record<string, string> = {
    units: 'Units on Hand',
    value: 'Inventory Value',
    skus: 'Unique SKUs',
}

export default function DepartmentPieChart({ data, metric = 'value' }: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const chartRef = useRef<any>(null)

    const colorMode = useThemeStore(s => s.getEffectiveColorMode())
    const textColor = colorMode === 'dark' ? 'rgba(255,255,255,0.7)' : '#475569'
    const tooltipBg = colorMode === 'dark' ? 'rgba(15,10,30,0.95)' : 'rgba(255,255,255,0.95)'
    const tooltipTitle = colorMode === 'dark' ? '#fff' : '#0f172a'
    const tooltipBody = colorMode === 'dark' ? 'rgba(255,255,255,0.8)' : '#334155'
    const tooltipBorder = colorMode === 'dark' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(168, 85, 247, 0.15)'

    // Sort descending and group small slices into "Other"
    const sorted = [...data].sort((a, b) => getMetricValue(b, metric) - getMetricValue(a, metric))
    const total = sorted.reduce((s, d) => s + getMetricValue(d, metric), 0)

    const TOP_N = 8
    const significant: DepartmentSlice[] = []
    let otherSlice: DepartmentSlice = { department: 'Other', totalQuantity: 0, inventoryValue: 0, uniqueSKUs: 0 }
    sorted.forEach((d, i) => {
        if (i < TOP_N && (getMetricValue(d, metric) / total) >= 0.02) {
            significant.push(d)
        } else {
            otherSlice.totalQuantity += d.totalQuantity
            otherSlice.inventoryValue += d.inventoryValue
            otherSlice.uniqueSKUs += d.uniqueSKUs
        }
    })
    if (getMetricValue(otherSlice, metric) > 0) significant.push(otherSlice)

    const labels = significant.map(d => d.department || 'Uncategorized')
    const values = significant.map(d => getMetricValue(d, metric))
    const colors = significant.map((_, i) => PALETTE[i % PALETTE.length])

    useEffect(() => {
        if (!canvasRef.current) return

        if (chartRef.current) {
            chartRef.current.destroy()
            chartRef.current = null
        }

        const ctx = canvasRef.current.getContext('2d')
        if (!ctx) return

        chartRef.current = new Chart(ctx, {
            type: 'pie',
            data: {
                labels,
                datasets: [{
                    data: values,
                    backgroundColor: colors,
                    borderColor: colorMode === 'dark' ? 'rgba(15,10,30,0.8)' : 'rgba(255,255,255,0.9)',
                    borderWidth: 2,
                    hoverBorderWidth: 3,
                    hoverOffset: 6,
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 4, bottom: 4, left: 4, right: 4 } },
                plugins: {
                    legend: {
                        position: 'right',
                        labels: {
                            color: textColor,
                            font: { size: 11 },
                            boxWidth: 12,
                            padding: 8,
                            usePointStyle: true,
                            pointStyle: 'circle',
                            generateLabels: (chart: any) => {
                                const ds = chart.data.datasets[0]
                                const d = ds.data as number[]
                                return (chart.data.labels as string[]).map((label, i) => {
                                    const pct = total > 0 ? ((d[i] / total) * 100).toFixed(1) : '0'
                                    return {
                                        text: `${label}  ${pct}%`,
                                        fillStyle: (ds.backgroundColor as string[])[i],
                                        strokeStyle: 'transparent',
                                        hidden: false,
                                        index: i,
                                        fontColor: textColor,
                                        pointStyle: 'circle' as const,
                                    }
                                })
                            }
                        }
                    },
                    tooltip: {
                        backgroundColor: tooltipBg,
                        titleColor: tooltipTitle,
                        bodyColor: tooltipBody,
                        borderColor: tooltipBorder,
                        borderWidth: 1,
                        cornerRadius: 8,
                        padding: 10,
                        callbacks: {
                            title: (items: any[]) => items[0]?.label || '',
                            label: (item: any) => {
                                const val = item.parsed
                                const pct = total > 0 ? ((val / total) * 100).toFixed(1) : '0'
                                return `${METRIC_LABELS[metric]}: ${formatValue(val, metric)}  (${pct}%)`
                            }
                        }
                    }
                }
            }
        })

        return () => {
            if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null }
        }
    }, [data, metric, colorMode])

    return (
        <div className="chart-panel" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="chart-panel-title" style={{ padding: '12px 16px 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                Inventory by Department
            </div>
            <div style={{ flex: 1, minHeight: 0, padding: '8px 12px 12px', position: 'relative' }}>
                <canvas ref={canvasRef} />
            </div>
        </div>
    )
}
